import { type Skill } from '../../skills';

export const SKILL_WORD_UNIQUE_MIN_CHARS = 3;
export const SKILL_WORD_MULTI_MIN_CHARS = 5;
export const SKILL_WORD_MAX_SUGGESTIONS = 3;
/** Trailing words considered together, so "black and white" stays one phrase. */
export const SKILL_WORD_PHRASE_MAX_WORDS = 6;

export type SkillWordMatch = {
  from: number;
  to: number;
  query: string;
  skills: Skill[];
};

/**
 * Extra phrases for a slash command, supplied by the caller.
 * Chat omits them. The null state passes rail titles and keywords.
 */
export type SkillSearchAlias = {
  keywords: readonly string[];
  slashCommand: string;
  titles: readonly string[];
};

const WORD_CHAR_RE = /[a-zA-Z0-9-]/;

const stripSeparators = (value: string): string => value.replace(/[\s-]/g, '');

const startsWithPrefix = (value: string, prefix: string): boolean => value.startsWith(prefix);

const normalizePhrase = (value: string): string => value.trim().toLowerCase().replace(/\s+/g, ' ');

/** True when `query` is a prefix of `haystack` or of any word-aligned suffix of it. */
const phraseIsPrefixOf = (haystack: string, query: string): boolean => {
  const normalizedQuery = normalizePhrase(query);

  if (!normalizedQuery) {
    return false;
  }

  const words = normalizePhrase(haystack).split(' ');

  return words.some((_, index) => words.slice(index).join(' ').startsWith(normalizedQuery));
};

type MatchRank = 0 | 1 | 2 | 3 | 4;

const MATCH_RANK_SLASH_COMMAND_PREFIX: MatchRank = 0;
const MATCH_RANK_SLASH_COMMAND_STRIPPED: MatchRank = 1;
const MATCH_RANK_NAME_PREFIX: MatchRank = 2;
const MATCH_RANK_NAME_WORD: MatchRank = 3;
const MATCH_RANK_KEYWORD: MatchRank = 4;

const getMatchRank = (skill: Skill, query: string): MatchRank | null => {
  const normalizedQuery = normalizePhrase(query);

  if (!normalizedQuery) {
    return null;
  }

  const lowerCommand = skill.slashCommand.toLowerCase();

  if (startsWithPrefix(lowerCommand, normalizedQuery)) {
    return MATCH_RANK_SLASH_COMMAND_PREFIX;
  }

  if (startsWithPrefix(stripSeparators(lowerCommand), stripSeparators(normalizedQuery))) {
    return MATCH_RANK_SLASH_COMMAND_STRIPPED;
  }

  const normalizedName = normalizePhrase(skill.name);

  if (startsWithPrefix(normalizedName, normalizedQuery)) {
    return MATCH_RANK_NAME_PREFIX;
  }

  if (phraseIsPrefixOf(skill.name, normalizedQuery)) {
    return MATCH_RANK_NAME_WORD;
  }

  return null;
};

const getAliasRank = (alias: SkillSearchAlias, query: string): MatchRank | null => {
  if (alias.titles.some((title) => phraseIsPrefixOf(title, query))) {
    return MATCH_RANK_NAME_WORD;
  }

  // Short keywords ("text", "gif") would fire on ordinary sentences. Names still match at 3.
  if (normalizePhrase(query).length < SKILL_WORD_MULTI_MIN_CHARS) {
    return null;
  }

  if (alias.keywords.some((keyword) => phraseIsPrefixOf(keyword, query))) {
    return MATCH_RANK_KEYWORD;
  }

  return null;
};

const getBestRank = (skill: Skill, query: string, aliases: readonly SkillSearchAlias[]): MatchRank | null => {
  const nameRank = getMatchRank(skill, query);
  const alias = aliases.find((entry) => entry.slashCommand.toLowerCase() === skill.slashCommand.toLowerCase());
  const aliasRank = alias ? getAliasRank(alias, query) : null;

  if (nameRank === null) {
    return aliasRank;
  }

  if (aliasRank === null) {
    return nameRank;
  }

  // Math.min is typed as number. Compare back to the ranks so the result stays a MatchRank.
  const bestRank = Math.min(nameRank, aliasRank);

  if (bestRank === nameRank) {
    return nameRank;
  }

  return aliasRank;
};

type CursorQuery = {
  from: number;
  query: string;
  to: number;
};

/**
 * Extracts the word immediately before the cursor within the current textblock.
 * `textBeforeCursorInParent` is text from the block start to the cursor
 * (e.g. ProseMirror `$from.start()` to `$from.pos`).
 * A trailing space returns null here. Suggestions use `getTypedPhraseAtCursor` instead,
 * so the space between words of a name does not clear the pill.
 * Callers must also verify the cursor is at a word boundary (no word chars after it)
 * via `resolveSkillWordSuggestion` — mid-word carets must not suggest.
 */
export const getTypedWordAtCursor = (textBeforeCursorInParent: string, cursorPos: number): CursorQuery | null => {
  let start = textBeforeCursorInParent.length;

  while (start > 0 && WORD_CHAR_RE.test(textBeforeCursorInParent[start - 1] ?? '')) {
    start -= 1;
  }

  const query = textBeforeCursorInParent.slice(start);

  if (!query) {
    return null;
  }

  return {
    from: cursorPos - query.length,
    query,
    to: cursorPos,
  };
};

/**
 * The last few words at the caret, including a trailing space.
 * The space stays in the range so accepting the pill replaces "black and white " and not just "white".
 */
export const getTypedPhraseAtCursor = (textBeforeCursorInParent: string, cursorPos: number): CursorQuery | null => {
  let index = textBeforeCursorInParent.length;

  while (index > 0 && textBeforeCursorInParent[index - 1] === ' ') {
    index -= 1;
  }

  let wordCount = 0;
  let phraseStart = index;

  while (index > 0 && wordCount < SKILL_WORD_PHRASE_MAX_WORDS) {
    if (!WORD_CHAR_RE.test(textBeforeCursorInParent[index - 1] ?? '')) {
      break;
    }

    while (index > 0 && WORD_CHAR_RE.test(textBeforeCursorInParent[index - 1] ?? '')) {
      index -= 1;
    }

    phraseStart = index;
    wordCount += 1;

    if (wordCount >= SKILL_WORD_PHRASE_MAX_WORDS) {
      break;
    }

    let beforeSpaces = index;

    while (beforeSpaces > 0 && textBeforeCursorInParent[beforeSpaces - 1] === ' ') {
      beforeSpaces -= 1;
    }

    if (beforeSpaces === index || !WORD_CHAR_RE.test(textBeforeCursorInParent[beforeSpaces - 1] ?? '')) {
      break;
    }

    index = beforeSpaces;
  }

  if (wordCount === 0) {
    return null;
  }

  const query = textBeforeCursorInParent.slice(phraseStart);

  return {
    from: cursorPos - query.length,
    query,
    to: cursorPos,
  };
};

/** True when the character immediately after the cursor is still part of the same word. */
export const hasWordCharAfterCursor = (textAfterCursorInParent: string): boolean =>
  textAfterCursorInParent.length > 0 && WORD_CHAR_RE.test(textAfterCursorInParent[0] ?? '');

/**
 * Case-insensitive prefix match on slashCommand (raw, or with spaces and hyphens removed)
 * and name (full name or any word-aligned suffix). Aliases add rail titles at the name-suffix
 * rank and keywords one rank lower. Does not match description.
 */
export const matchSkillsByTypedWord = (
  skills: Skill[],
  query: string,
  aliases: readonly SkillSearchAlias[] = [],
): Skill[] => {
  const trimmed = query.trim();

  if (!trimmed) {
    return [];
  }

  const ranked = skills
    .map((skill, index) => {
      const rank = getBestRank(skill, trimmed, aliases);

      if (rank === null) {
        return null;
      }

      return { index, rank, skill };
    })
    .filter((entry): entry is { skill: Skill; rank: MatchRank; index: number } => entry !== null);

  ranked.sort((a, b) => {
    if (a.rank !== b.rank) {
      return a.rank - b.rank;
    }
    const lengthDiff = a.skill.slashCommand.length - b.skill.slashCommand.length;

    if (lengthDiff !== 0) {
      return lengthDiff;
    }

    return a.index - b.index;
  });

  return ranked.map((entry) => entry.skill);
};

/**
 * Applies character-count thresholds for the skill suggestion pill.
 * Unique match from 3+ chars; multi matches only from 5+ chars (capped).
 * The count ignores leading and trailing spaces, so "black " still counts as 5.
 */
export const getSkillWordSuggestions = (
  skills: Skill[],
  query: string,
  aliases: readonly SkillSearchAlias[] = [],
): Skill[] => {
  const trimmed = query.trim();

  if (!trimmed || trimmed.startsWith('/') || trimmed.length < SKILL_WORD_UNIQUE_MIN_CHARS) {
    return [];
  }

  const matches = matchSkillsByTypedWord(skills, trimmed, aliases);

  if (matches.length === 0) {
    return [];
  }

  if (matches.length === 1) {
    return matches;
  }

  if (trimmed.length < SKILL_WORD_MULTI_MIN_CHARS) {
    return [];
  }

  return matches.slice(0, SKILL_WORD_MAX_SUGGESTIONS);
};

const suffixStartInPhrase = (phrase: string, wordCount: number): number => {
  const end = phrase.trimEnd().length;
  const wordStarts: number[] = [];
  let index = 0;

  while (index < end) {
    while (index < end && phrase[index] === ' ') {
      index += 1;
    }

    if (index >= end) {
      break;
    }

    wordStarts.push(index);

    while (index < end && phrase[index] !== ' ') {
      index += 1;
    }
  }

  return wordStarts[wordStarts.length - wordCount] ?? 0;
};

export const resolveSkillWordSuggestion = (args: {
  /** Rail titles and keywords. Omitted by chat, which matches skill names only. */
  aliases?: readonly SkillSearchAlias[];
  skills: Skill[];
  textBeforeInBlock: string;
  /** Text from the cursor to the end of the current textblock (`$from.pos` to `$from.end()`). */
  textAfterInBlock: string;
  cursorPos: number;
}): SkillWordMatch | null => {
  // Mid-word caret: accepting would only replace the prefix and leave orphan trailing chars.
  if (hasWordCharAfterCursor(args.textAfterInBlock)) {
    return null;
  }

  const phrase = getTypedPhraseAtCursor(args.textBeforeInBlock, args.cursorPos);

  if (!phrase) {
    return null;
  }

  const words = phrase.query
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0);
  const aliases = args.aliases ?? [];
  let wordCount = words.length;

  while (wordCount > 0) {
    const suffixQuery = words.slice(words.length - wordCount).join(' ');
    const skills = getSkillWordSuggestions(args.skills, suffixQuery, aliases);

    if (skills.length > 0) {
      const suffixStart = suffixStartInPhrase(phrase.query, wordCount);

      return {
        from: phrase.from + suffixStart,
        query: phrase.query.slice(suffixStart),
        skills,
        to: phrase.to,
      };
    }

    wordCount -= 1;
  }

  return null;
};
